/**
 * ConditionCodeList — Renders COND_* and REMED_* codes with human-readable descriptions.
 *
 * Labels are sourced from `PRE_CONDITION_LABELS` and `PRE_REMEDIATION_LABELS` in
 * `@respectly/shared-types`. Those maps are typed `Record<UnionType, string>` so
 * adding a new condition or remediation code without a label is a TypeScript
 * compile error — no Decision-View component maintains its own label subset.
 */
import { Badge, cn } from '@respectly/ui';
import { Info, AlertCircle } from 'lucide-react';
import {
  PRE_CONDITION_LABELS,
  PRE_REMEDIATION_LABELS,
  type PREConditionCode,
  type PRERemediationCode,
} from '@respectly/shared-types';

interface Condition {
  code: string;
  params?: Record<string, unknown>;
  description?: string;
}

interface ConditionCodeListProps {
  conditions: Condition[];
  className?: string;
}

function isRemediation(code: string) {
  return code.startsWith('REMED_');
}

export function ConditionCodeList({ conditions, className }: ConditionCodeListProps) {
  if (!conditions.length) return null;

  return (
    <div className={cn('space-y-1.5', className)}>
      {conditions.map((cond, i) => {
        const isRemed = isRemediation(cond.code);
        const label = isRemed
          ? PRE_REMEDIATION_LABELS[cond.code as PRERemediationCode]
          : PRE_CONDITION_LABELS[cond.code as PREConditionCode];
        const Icon = isRemed ? AlertCircle : Info;

        return (
          <div key={`${cond.code}-${i}`} className="flex items-start gap-2">
            <Badge
              variant={isRemed ? 'error' : 'warning'}
              className="flex-shrink-0 mt-0.5"
            >
              <Icon className="w-3 h-3 mr-1" />
              {label ?? cond.code}
            </Badge>
            {cond.description && (
              <span className="text-xs text-slate-500 dark:text-slate-400 pt-0.5">
                {cond.description}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
